import { SCOPES_CONHECIDOS, Scope } from '@acme/core'
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    FormLabel,
    Grid,
    Typography,
} from '@mui/material'
import { FormField, FormFieldSelect } from 'global/components/Form'
import React from 'react'
import { useFormContext } from 'react-hook-form'

export const SCOPE_DESCRIPTIONS: Record<
    Scope,
    { label: string; description: string }
> = {
    'registros:read': {
        label: 'Registros de Ponto (leitura)',
        description: 'Consulta marcações e batidas de ponto dos funcionários.',
    },
    'funcionarios:read': {
        label: 'Funcionários (leitura)',
        description:
            'Consulta dados cadastrais de funcionários e vínculos de locais.',
    },
    'calculo:read': {
        label: 'Cálculo e Espelho (leitura)',
        description: 'Consulta apuração do cálculo e espelho de ponto.',
    },
    'saldo-horas:read': {
        label: 'Saldo de Horas (leitura)',
        description: 'Consulta saldo de horas e extrato de banco de horas.',
    },
}

interface CredencialFormProps {
    isEditing?: boolean
    /**
     * Mostra o campo de cota (RPM). A cota é decisão comercial da Farol, não do
     * cliente — só Administrador e Suporte editam, pela tela por linha em
     * Administração. A API descarta o campo de quem não tem alçada, então
     * exibi-lo aqui faria o cliente preencher algo sem efeito.
     */
    podeEditarCota?: boolean
}

export const CredencialForm: React.FC<CredencialFormProps> = ({
    isEditing = false,
    podeEditarCota = false,
}) => {
    const {
        watch,
        setValue,
        formState: { errors },
    } = useFormContext()

    const selectedScopes = (watch('scopes') || []) as Scope[]

    const handleToggleScope = (scope: Scope) => {
        const nextScopes = selectedScopes.includes(scope)
            ? selectedScopes.filter(s => s !== scope)
            : [...selectedScopes, scope]
        setValue('scopes', nextScopes, {
            shouldValidate: false,
            shouldDirty: true,
        })
    }

    const scopesError = errors.scopes?.message as string | undefined

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <FormField
                    name="nome"
                    label="Nome da Aplicação / Integração"
                    placeholder="Ex: PowerBI Diretoria, ERP Senior, Totvs"
                    required
                />
            </Grid>

            {podeEditarCota && (
                <Grid item xs={12} sm={isEditing ? 6 : 12}>
                    <FormField
                        name="rateLimitRpm"
                        label="Limite de Requisições por Minuto (RPM)"
                        type="number"
                        defaultValue={60}
                        required
                    />
                </Grid>
            )}

            {isEditing && (
                <Grid item xs={12} sm={podeEditarCota ? 6 : 12}>
                    <FormFieldSelect
                        name="ativo"
                        label="Status"
                        options={[
                            { label: 'Ativo', value: 'S' },
                            { label: 'Inativo', value: 'N' },
                        ]}
                    />
                </Grid>
            )}

            <Grid item xs={12}>
                <FormControl
                    component="fieldset"
                    error={Boolean(scopesError)}
                    fullWidth
                >
                    <FormLabel
                        component="legend"
                        className="font-medium text-sm mb-2"
                    >
                        Escopos de Acesso Permitidos *
                    </FormLabel>
                    <FormGroup className="flex flex-col gap-2">
                        {SCOPES_CONHECIDOS.map(scope => {
                            const info = SCOPE_DESCRIPTIONS[scope]
                            const checked = selectedScopes.includes(scope)
                            return (
                                <Box
                                    key={scope}
                                    className={`p-3 rounded border transition-colors cursor-pointer ${
                                        checked
                                            ? 'border-primary bg-primary/5'
                                            : 'border-border hover:bg-muted/30'
                                    }`}
                                    onClick={() => handleToggleScope(scope)}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={checked}
                                                onChange={() =>
                                                    handleToggleScope(scope)
                                                }
                                                onClick={e =>
                                                    e.stopPropagation()
                                                }
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography
                                                    variant="subtitle2"
                                                    className="font-semibold"
                                                >
                                                    {info.label} ({scope})
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    className="text-xs mt-0.5"
                                                >
                                                    {info.description}
                                                </Typography>
                                            </Box>
                                        }
                                        className="m-0 select-none items-start"
                                    />
                                </Box>
                            )
                        })}
                    </FormGroup>
                    {scopesError && (
                        <FormHelperText error className="mt-2">
                            {scopesError}
                        </FormHelperText>
                    )}
                </FormControl>
            </Grid>
        </Grid>
    )
}
